import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { DropdownButton } from "react-bootstrap";

import ReactTable from "@components/ReactTable";
import { AngleDownIcon } from "@components/icons";
import { statusHeader, batteryHeader, keyHeader } from "@modules/Sensors/components/SensorList";

export default function SensorList({ sensors }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const query = new URLSearchParams(location.search);
  const id = query.get("selectedSensor");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [ordering, setOrdering] = useState("");
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);

  const columns = useMemo(
    () => [
      statusHeader(t),
      keyHeader(t),
      {
        Header: `${t("name")}`,
        accessor: "name",
      },
      {
        Header: `${t("tree")}`,
        accessor: "tree",
      },
      batteryHeader(t),
    ],
    []
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const areaOptions = useMemo(() => {
    const byId = new Map();
    (sensors || []).forEach((sensor) => {
      const id = String(sensor.areaId || "");
      const name = sensor.area || "";
      if (!id || !name || byId.has(id)) return;
      byId.set(id, { id, name });
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [sensors]);

  const filterSensorsByArea = (areaId) => {
    setSelectedAreaIds((prev) =>
      prev.includes(areaId) ? prev.filter((id) => id !== areaId) : [...prev, areaId]
    );
    setPage(1);
  };

  const filteredSensors = useMemo(() => {
    const term = search.trim().toLowerCase();
    let result = sensors;

    if (selectedAreaIds.length > 0) {
      result = result.filter((sensor) => selectedAreaIds.includes(String(sensor.areaId || "")));
    }

    if (!term) return result;

    return result.filter((sensor) => {
      const haystack = [sensor.name, sensor.tree, sensor.hardware_serial, sensor.imei]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [sensors, search, selectedAreaIds]);

  const sortedSensors = useMemo(() => {
    if (!ordering) return filteredSensors;

    const isDesc = ordering.startsWith("-");
    const key = isDesc ? ordering.slice(1) : ordering;
    const direction = isDesc ? -1 : 1;

    return [...filteredSensors].sort((a, b) => {
      const aValue = a?.[key] ?? "";
      const bValue = b?.[key] ?? "";

      if (typeof aValue === "number" && typeof bValue === "number") {
        return (aValue - bValue) * direction;
      }

      return (
        String(aValue).localeCompare(String(bValue), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * direction
      );
    });
  }, [filteredSensors, ordering]);

  const pagedSensors = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedSensors.slice(start, start + pageSize);
  }, [sortedSensors, page, pageSize]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(sortedSensors.length / pageSize));
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [sortedSensors.length, pageSize, page]);

  const onRowClicked = (row) => {
    const { id } = row.original;
    const params = new URLSearchParams(location.search);
    params.set("selectedSensor", id);
    navigate(`${location.pathname}?${params.toString()}`);
  };

  return (
    <ReactTable
      columns={columns}
      data={pagedSensors}
      onRowClicked={onRowClicked}
      activeId={id}
      pagesize={pageSize}
      size={[10, 20, 30, 40, 50]}
      manualPagination
      pageCount={Math.max(1, Math.ceil(sortedSensors.length / pageSize))}
      currentPage={page}
      onPageChange={setPage}
      onPageSizeChange={(nextPageSize) => {
        setPageSize(nextPageSize);
        setPage(1);
      }}
      onSortChange={(nextOrdering) => {
        setOrdering(nextOrdering);
        setPage(1);
      }}
      searchValue={searchInput}
      onSearchChange={setSearchInput}
      searchPlaceholder={t("search_sensor")}
      moreFilter={
        areaOptions.length > 0 && (
          <DropdownButton
            title={
              <div className="d-flex gap-2">
                <span>{t("area")}</span>
                <AngleDownIcon />
              </div>
            }
            variant="btn-transparent"
          >
            {areaOptions.map((area) => (
              <li key={area.id} className="d-flex align-items-center gap-2 px-2 py-1">
                <input
                  type="checkbox"
                  onChange={() => filterSensorsByArea(area.id)}
                  checked={selectedAreaIds.includes(area.id)}
                />
                <span>{area.name}</span>
              </li>
            ))}
          </DropdownButton>
        )
      }
    />
  );
}

export const SensorStatus = ({ color, title }) => {
  return (
    <span
      className={"badge"}
      style={{ padding: "5px", borderRadius: "2px", background: color }}
      title={`${title}`}
    >
      <div
        className="rounded-circle"
        style={{
          width: "6px",
          height: "6px",
          background: "#fff",
        }}
      ></div>
    </span>
  );
};
